<template>
  <div v-if="visible" class="vtk-modal-overlay">
    <div class="vtk-modal-content">
      <div class="vtk-modal-header mb-3">
        <h2 class="vtk-modal-title">Frontal Face Adjustment</h2>
        <p class="vtk-modal-instruction">
          Please inspect the mesh and if the face is not in a frontal position,
          use the rotation buttons to adjust it before confirming.
        </p>
        <h4 class="example-title mb-1 mt-3">Examples:</h4>
        <div class="example-mesh-row">
          <div class="example-image">
            <img src="@/assets/example-frontal.png" alt="Correct Mesh" />
            <span class="status-icon">✔️</span>
          </div>
          <div class="example-image">
            <img src="@/assets/example-angular.png" alt="Tilted Mesh" />
            <span class="status-icon">❌</span>
          </div>
          <div class="example-image">
            <img src="@/assets/example-upside.png" alt="Rotated Mesh" />
            <span class="status-icon">❌</span>
          </div>
        </div>
      </div>

      <div class="vtk-render-section">
        <div ref="vtkContainer" class="vtk-renderer"></div>
        <div class="vtk-rotation-grid">
          <div class="axis-group">
            <span class="axis-label">X</span>
            <div class="axis-buttons">
              <button @click="rotateX(-10)">−</button>
              <button @click="rotateX(10)">+</button>
            </div>
          </div>
          <div class="axis-group">
            <span class="axis-label">Y</span>
            <div class="axis-buttons">
              <button @click="rotateY(-10)">−</button>
              <button @click="rotateY(10)">+</button>
            </div>
          </div>
          <div class="axis-group">
            <span class="axis-label">Z</span>
            <div class="axis-buttons">
              <button @click="rotateZ(-10)">−</button>
              <button @click="rotateZ(10)">+</button>
            </div>
          </div>
        </div>
      </div>

      <div class="vtk-modal-actions">
        <button type="button" @click="close" class="btn cancel-btn">
          Cancel
        </button>
        <button type="button" @click="confirm" class="btn confirm-btn">
          Confirm
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import "@kitware/vtk.js/Rendering/Profiles/Geometry";
import vtkOBJReader from "@kitware/vtk.js/IO/Misc/OBJReader";
import vtkMapper from "@kitware/vtk.js/Rendering/Core/Mapper";
import vtkActor from "@kitware/vtk.js/Rendering/Core/Actor";
import vtkRenderWindow from "@kitware/vtk.js/Rendering/Core/RenderWindow";
import vtkRenderer from "@kitware/vtk.js/Rendering/Core/Renderer";
import vtkRenderWindowInteractor from "@kitware/vtk.js/Rendering/Core/RenderWindowInteractor";
import vtkInteractorStyleTrackballCamera from "@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera";
import vtkOpenGLRenderWindow from "@kitware/vtk.js/Rendering/OpenGL/RenderWindow";

export default {
  props: {
    file: File,
    visible: Boolean,
  },
  emits: ["confirmed", "closed"],
  data() {
    return {
      renderer: null,
      renderWindow: null,
      reader: null,
      actor: null,
      originalVTLines: "",
      originalFLines: [],
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadMesh();
      }
    },
  },
  methods: {
    rotateX(angle) {
      this.actor.rotateX(angle);
      this.renderWindow.render();
    },
    rotateY(angle) {
      this.actor.rotateY(angle);
      this.renderWindow.render();
    },
    rotateZ(angle) {
      this.actor.rotateZ(angle);
      this.renderWindow.render();
    },

    cleanupRenderer() {
        if (this.renderWindow) {
            this.renderWindow.delete();
            this.renderWindow = null;
        }
        if (this.renderer) {
            this.renderer.delete();
            this.renderer = null;
        }
        const container = this.$refs.vtkContainer;
        if (container) {
            container.innerHTML = "";
        }
        this.reader = null;
        this.actor = null;
        },


    async loadMesh() {
      this.cleanupRenderer();

      const arrayBuffer = await this.file.arrayBuffer();
      const textDecoder = new TextDecoder();
      const objContent = textDecoder.decode(arrayBuffer);

      const lines = objContent.split("\n");
      this.originalVTLines = lines
        .filter((line) => line.startsWith("vt "))
        .join("\n");
      this.originalFLines = lines.filter((line) => line.startsWith("f "));

      this.reader = vtkOBJReader.newInstance();
      this.reader.parseAsText(objContent);

      const polyData = this.reader.getOutputData(0);

      const mapper = vtkMapper.newInstance();
      mapper.setInputData(polyData);

      this.actor = vtkActor.newInstance();
      this.actor.setMapper(mapper);

      this.renderer = vtkRenderer.newInstance();
      this.renderer.setBackground(1, 1, 1); 
      this.renderer.addActor(this.actor);
      this.renderer.resetCamera();

      this.renderWindow = vtkRenderWindow.newInstance();
      this.renderWindow.addRenderer(this.renderer);

      const openGLRenderWindow = vtkOpenGLRenderWindow.newInstance();
      this.renderWindow.addView(openGLRenderWindow);

      await this.$nextTick(); // Ensure container is mounted
      const container = this.$refs.vtkContainer;
      openGLRenderWindow.setContainer(container);
      openGLRenderWindow.setSize(container.clientWidth, container.clientHeight);

      const interactor = vtkRenderWindowInteractor.newInstance();
      interactor.setView(openGLRenderWindow);
      interactor.initialize();
      interactor.bindEvents(container);
      interactor.setInteractorStyle(
        vtkInteractorStyleTrackballCamera.newInstance()
      );

      this.renderWindow.render();
    },

    close() {
      this.$emit("closed");
    },
    confirm() {
      const polyData = this.reader.getOutputData(0);
      const points = polyData.getPoints();
      const numberOfPoints = points.getNumberOfPoints();

      const transformedPoints = [];
      const transformMatrix = this.actor.getMatrix();

      for (let i = 0; i < numberOfPoints; i++) {
        const pt = [...points.getTuple(i)];
       
        pt.push(1.0);

        const transformed = [0, 0, 0, 0];
        for (let row = 0; row < 4; row++) {
          for (let col = 0; col < 4; col++) {
            transformed[row] += transformMatrix[row * 4 + col] * pt[col];
          }
        }
        transformedPoints.push([
          transformed[0],
          transformed[1],
          transformed[2],
        ]);
      }

      let objText = "";
      transformedPoints.forEach(([x, y, z]) => {
        objText += `v ${x} ${y} ${z}\n`;
      });

      if (this.originalVTLines) {
        objText += this.originalVTLines + "\n";
      }
      this.originalFLines.forEach((line) => {
        const cleaned = line.replace(/(\d+)\/(\d+)\/(\d+)/g, "$1/$2");
        objText += cleaned + "\n";
      });

      const blob = new Blob([objText], { type: "text/plain" });
      this.$emit("confirmed", blob);
    },
  },
};
</script>

<style scoped>
.vtk-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.vtk-modal-content {
  background: #fff;
  padding: 10px;
  width: 550px;
  display: flex;
  flex-direction: column;
  border: 2px solid #ccc;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.vtk-render-section {
  border: 2px solid rgba(28 57 131 / 22%);
  border-radius: 8px;
  padding: 10px;
  background-color: #f9f9f9;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 380px;
  overflow: hidden;
}
.vtk-renderer {
  width: 100%;
  height: 300px;
}
.vtk-modal-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.confirm-btn {
  background-color: #2bc551;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
}

.confirm-btn:hover {
  background-color: #24a944;
}

.cancel-btn {
  background-color: #e0e0e0;
  color: #333;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
}

.cancel-btn:hover {
  background-color: #c8c8c8;
}
.vtk-modal-header {
  text-align: left;
}

.vtk-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1c3983;
  margin-bottom: 4px;
}

.vtk-modal-instruction {
  font-size: 14px;
  font-weight: 400;
  color: #333;
  margin: 0;
}

.vtk-rotation-grid {
  display: flex;
  justify-content: space-around;
  gap: 50px;
}

.axis-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.axis-label {
  font-weight: 600;
  margin-bottom: 4px;
  color: #003574;
}

.axis-buttons button {
  margin: 2px;
  padding: 4px 10px;
  font-size: 14px;
  font-weight: bold;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background-color: #f0f0f0;
}

.axis-buttons button:hover {
  background-color: #ccc;
}
.example-mesh-row {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 10px;
}

.example-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30%;
  justify-content: space-between;
}

.example-image img {
  width: 100%;
  height: 100px;
  object-fit: contain;
  border: 1px solid #ccc;
  border-radius: 6px;
}

.status-icon {
  font-size: 15px;
  margin-top: 8px;
  color: #6c6c6c;
}
.example-title {
  font-size: 15px;
  font-weight: 600;
  color: #1c3983;
}
</style>
